import React, { useState, useEffect } from "react";
import api from "../../../api/axiosConfig";
import Loader from "../../Shared/Loader";
import "../../../css/OverrideStationModal.css";

interface OverrideStationModalProps {
  type: "day" | "slot";
  targetDate: string;
  targetSlot: any | null;
  centerId: string;
  appointmentClusterId: string;
  effectiveStations: any[];
  onClose: () => void;
  onSuccess: () => void;
}

const OverrideStationModal: React.FC<OverrideStationModalProps> = ({
  type,
  targetDate,
  targetSlot,
  centerId,
  appointmentClusterId,
  effectiveStations,
  onClose,
  onSuccess,
}) => {
  const [staffList, setStaffList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // mapping stationId to selected entraObjectId
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  // mapping stationId to conflict data array
  const [conflicts, setConflicts] = useState<Record<string, any[]>>({});

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = sessionStorage.getItem("accessToken");
        const res = await api.auth_api.get("/users/with-roles/full", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setStaffList(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch staff:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStaff();
  }, []);

  const handleToggleOverride = (stationId: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (next[stationId] !== undefined) {
        delete next[stationId];
      } else {
        next[stationId] = "";
      }
      return next;
    });
    setConflicts((prev) => {
      const next = { ...prev };
      delete next[stationId];
      return next;
    });
  };

  const handleSelectStaff = (stationId: string, entraObjectId: string) => {
    setOverrides((prev) => ({ ...prev, [stationId]: entraObjectId }));
    setConflicts((prev) => {
      const next = { ...prev };
      delete next[stationId];
      return next;
    });
  };

  const handleSubmit = async () => {
    const activeOverrides = Object.entries(overrides).filter(
      ([_, userId]) => userId !== "",
    );
    if (activeOverrides.length === 0) {
      alert(
        "Please enable override and select a staff member for at least one station.",
      );
      return;
    }

    setSaving(true);
    setConflicts({});

    let hasConflicts = false;
    const newConflicts: Record<string, any[]> = {};

    try {
      const token = sessionStorage.getItem("accessToken");

      const checkPromises = activeOverrides.map(
        async ([stationId, entraObjectId]) => {
          try {
            const res = await api.center_api.get(
              `/api/v1/center/users/${entraObjectId}/availability?startDate=${targetDate}&endDate=${targetDate}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const rawAvailability = res.data?.data || [];

            const actualConflicts = rawAvailability
              .map((conflict: any) => {
                if (type === "slot" && targetSlot) {
                  const reqStart = targetSlot.start_time;
                  const reqEnd = targetSlot.end_time;

                  const overlappingSlots = conflict.slots.filter(
                    (existingSlot: any) => {
                      if (existingSlot.date !== targetDate) return false;
                      // Time overlap condition: StartA < EndB AND StartB < EndA
                      return (
                        reqStart < existingSlot.endTime &&
                        existingSlot.startTime < reqEnd
                      );
                    },
                  );

                  return { ...conflict, slots: overlappingSlots };
                } else {
                  // Day Override
                  const sameDateSlots = conflict.slots.filter(
                    (existingSlot: any) => existingSlot.date === targetDate,
                  );
                  return { ...conflict, slots: sameDateSlots };
                }
              })
              .filter(
                (conflict: any) => conflict.slots && conflict.slots.length > 0,
              );

            if (actualConflicts.length > 0) {
              newConflicts[stationId] = actualConflicts;
              hasConflicts = true;
            }
          } catch (err) {
            console.error(
              `Conflict check failed for user ${entraObjectId}`,
              err,
            );
          }
        },
      );

      await Promise.all(checkPromises);

      if (hasConflicts) {
        setConflicts(newConflicts);
        setSaving(false);
        return;
      }

      const stationsPayload = activeOverrides.map(
        ([stationId, entraObjectId]) => {
          const stationInfo = effectiveStations.find(
            (s) => s.stationId === stationId,
          );
          const staffInfo = staffList.find(
            (s) => s.entraObjectId === entraObjectId,
          );
          return {
            stationId: stationId,
            stationName: stationInfo?.stationName || "",
            users: [
              {
                userId: entraObjectId,
                userName: staffInfo?.displayName || "Unknown",
                email: staffInfo?.email,
              },
            ],
          };
        },
      );

      let patchPayload: any = {};
      if (type === "day") {
        patchPayload = {
          date: targetDate,
          stations: stationsPayload,
        };
      } else {
        patchPayload = {
          date: targetDate,
          stations: [], // Added to satisfy backend validation
          slotOverrides: [
            {
              startTime: targetSlot?.start_time,
              endTime: targetSlot?.end_time,
              stations: stationsPayload,
            },
          ],
        };
      }

      await api.center_api.patch(
        `/api/v1/center/${centerId}/config/daily-override?appointmentClusterId=${appointmentClusterId}`,
        patchPayload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      onSuccess();
    } catch (err: any) {
      console.error("Failed to submit override:", err);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Unknown error";
      alert(`Failed to save overrides:\n\n${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="override-modal-overlay" onClick={onClose}>
      <div
        className="override-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="override-modal-header">
          <h2>
            Edit{" "}
            {type === "day"
              ? `Day: ${targetDate}`
              : `Slot: ${targetDate} ${targetSlot?.start_time} - ${targetSlot?.end_time}`}
          </h2>
          <button className="override-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        {loading ? (
          <Loader text="Loading staff..." />
        ) : (
          <div className="override-stations-list">
            {effectiveStations.map((station) => {
              const isOverridden = overrides[station.stationId] !== undefined;
              const selectedUserId = overrides[station.stationId] || "";
              const stationConflicts = conflicts[station.stationId];

              return (
                <div key={station.stationId} className="override-station-row">
                  <div className="override-station-main">
                    <div className="override-station-info">
                      <div className="override-station-name">
                        {station.stationName}
                      </div>
                      <div className="override-station-current">
                        Current:{" "}
                        {station.users
                          ?.map((u: any) => u.userName)
                          .join(", ") || "None"}
                      </div>
                    </div>

                    <div className="override-station-control">
                      <label className="override-checkbox-label">
                        <input
                          type="checkbox"
                          checked={isOverridden}
                          onChange={() =>
                            handleToggleOverride(station.stationId)
                          }
                        />
                        Override Staff?
                      </label>

                      {isOverridden && (
                        <select
                          className="override-select"
                          value={selectedUserId}
                          onChange={(e) =>
                            handleSelectStaff(station.stationId, e.target.value)
                          }
                        >
                          <option value="">Select Staff...</option>
                          {staffList.map((staff) => (
                            <option key={staff._id} value={staff.entraObjectId}>
                              {staff.displayName}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {stationConflicts && stationConflicts.length > 0 && (
                    <div className="conflict-alert">
                      <h4>User is already booked for:</h4>
                      {stationConflicts.map((c: any, idx: number) => (
                        <div key={idx} className="conflict-item">
                          <div className="conflict-center-name">
                            {c.centerName}
                          </div>
                          <div className="conflict-slots-container">
                            {c.slots?.map((s: any, sIdx: number) => (
                              <span key={sIdx} className="conflict-slot-badge">
                                {s.date} {s.startTime}-{s.endTime}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="override-modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-submit"
            onClick={handleSubmit}
            disabled={saving || loading}
          >
            {saving ? "Checking & Saving..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OverrideStationModal;
